import React, { useEffect, useState } from 'react';

import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { InstantSearch } from 'react-instantsearch';

/* Config */
import { defaultConfig } from '../../config/reactToastify/configs';

/* Icons */
import { SearchIcon } from "../../common/Icons";

/* Utils */
import { createSearchClient } from '../utils/searchClient';

/* Components */
import SearchOverlay from './SearchOverlay';
import { useLocalStorage } from "../../hooks/useLocalStorage";

const backdropVariants = {
    initial: {
        opacity: 0,
        transition: { ease: 'linear', duration: 0.15 },
    },
    animate: {
        opacity: 1,
        transition: { ease: 'linear', duration: 0.15 },
    },
    exit: {
        opacity: 0,
        transition: { ease: 'linear', duration: 0.15 },
    },
};

const overlayVariants = {
    initial: (isDesktop) => ({
        y: isDesktop ? 0 : -50,
        opacity: 0,
        transition: { ease: 'easeOut', duration: 0.3 },
    }),
    animate: {
        y: 0,
        opacity: 1,
        transition: { ease: 'easeOut', duration: 0.3 },
    },
    exit: (isDesktop) => ({
        y: isDesktop ? 0 : -50,
        opacity: 0,
        transition: { ease: 'easeOut', duration: 0.3 },
    }),
}

export default function Search(props) {
    const [isOpen, setIsOpen] = useState(false);
    const [isDesktop, setIsDesktop] = useState(window.matchMedia('(min-width: 992px)').matches);
    const [inputValue, setInputValue] = useState('');
    const [recents, setRecents] = useLocalStorage('recents', [], true);

    const mediaListener = (event) => {
        setIsDesktop(event.matches);
    };

    useEffect(() => {
        const matchMedia = window.matchMedia('(min-width: 992px)');
        matchMedia.addEventListener('change', mediaListener);
        toast.configure(defaultConfig);

        window.__searchErrorToast = () => {
            toast.error(window.Translator.trans('app.error_messages.search.global'));
        };

        return () => {
            matchMedia.removeEventListener('change', mediaListener);
            delete window.__searchErrorToast;
        }
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                closeSearch();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    const openSearch = () => {
        setIsOpen(true);
        setInputValue('');
        document.body.style.overflow = 'hidden';
    }

    const closeSearch = () => {
        addToRecents(inputValue);
        setIsOpen(false);
        document.body.style.overflow = '';
    }

    const addToRecents = (query) => {
        if (!query || query.length < 2) return;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'view_search_results',
            search_term: query
        });

        setRecents((recents) => {
            const filtered = recents.filter(r => r !== query);
            if (filtered.length >= 5) {
                filtered.splice(0, 1);
            }
            return [query, ...filtered];
        });
    };

    const searchClient = createSearchClient();

    if (!searchClient) return null;

    return (
        <InstantSearch searchClient={searchClient}>
            <div className="search">
                <button
                    className="btn btn-link text-base p-0"
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => openSearch()}
                >
                    <SearchIcon className="icon icon--24" />
                </button>
                <AnimatePresence initial={false} exitBeforeEnter>
                    {isOpen && (
                        <>
                            <motion.div
                                key="backdrop"
                                className="search__backdrop"
                                custom={isDesktop}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                variants={backdropVariants}
                                onClick={closeSearch}
                            />
                            <motion.div
                                key="overlay"
                                className="search__overlay"
                                custom={isDesktop}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                variants={overlayVariants}
                            >
                                <SearchOverlay
                                    inputValue={inputValue}
                                    setInputValue={setInputValue}
                                    closeSearch={closeSearch}
                                    recents={recents}
                                    typesenseConfig={props?.typesenseConfig}
                                    suluSearchData={props?.suluSearchData}
                                />
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </div>
        </InstantSearch>
    );
}
